import React, { forwardRef, useEffect, useState } from 'react';
import { Badge, Typography, theme } from 'antd';
import { IconAdjustmentsHorizontal, IconArrowLeft, IconMap2, IconSearchOff, IconX } from '@tabler/icons-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HomeSearchBar, { HomeSearchState } from '../../components/HomeSearchBar';
import MapCategoryPicker, { MapExploreCategory } from '../../components/MapCategoryPicker';
import SpacesMap, { IMapBounds, SpacesMapHandle } from '../../components/SpacesMap';
import SnapBottomSheet, { MEDIA_SNAP_RATIO, SheetSnapPoint } from '../../components/SnapBottomSheet';
import SwipeCardCarousel from '../../components/SwipeCardCarousel';
import ListPagination from '../../components/ListPagination';
import GearLoadingState from '../../components/GearLoadingState';
import EmptyState from '../../components/EmptyState';
import { useAuth } from '../../contextProviders/AuthProvider';
import { useMapCategoryViews } from '../../hooks/useMapCategoryViews';
import { CORAL } from '../../theme/creamyTokens';
import { IHeavyDutyMachinery } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Text } = Typography;

// This screen's own fixed header height -- reserved on the sheet itself via
// SnapBottomSheet's headerClearance prop, and matches antd Layout.Header's
// default (what AppHeader/LandingLayout would otherwise show here) so
// nothing about the overall page rhythm changes even though this screen now
// renders its own header instead of theirs (see hideHeader on both, wired
// from HeavyDutyMachineries.tsx).
const OWN_HEADER_HEIGHT = 64;
// Matches MobileBottomNav's own fixed tab-row height.
const BOTTOM_NAV_HEIGHT = 56;
// Keeps the compact locate/satellite stack (SpacesMap's mobileControls,
// top-right) clear of this screen's own fixed header, which sits above the
// map at a higher z-index (see OWN_HEADER_HEIGHT/the header block below).
const MAP_CONTROLS_TOP_INSET = OWN_HEADER_HEIGHT + 16;

interface ExploreMobileMapProps {
  items: IHeavyDutyMachinery[];
  totalCount: number;
  loading: boolean;
  // Forwarded straight to SpacesMap -- see its own prop doc for why this
  // (not lat/lng) is what drives the fit-to-results camera refit.
  locationSearchSeq: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  searchBarInitialValues: HomeSearchState;
  onSearchBarSubmit: (values: HomeSearchState) => void;
  onBoundsChange: (bounds: IMapBounds) => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
  onOpenSpace: (spaceId: string) => void;
  // Lifted to HeavyDutyMachineries.tsx -- it also needs to know the current snap
  // point to decide whether MobileBottomNav should render at all (see
  // hideBottomNav there), so this can't just be local state here anymore.
  snapPoint: SheetSnapPoint;
  onSnapPointChange: (point: SheetSnapPoint) => void;
}

/**
 * /explore's mobile (<768px) view: an Airbnb-style map+list, replacing the
 * plain stacked-cards layout tablet/desktop-below-lg used to fall back to
 * (see HeavyDutyMachineries.tsx -- that fallback is now mobile-only via
 * useAppBreakpoint's `isMobile`, tablet still gets the old cards-only view
 * untouched). SpacesMap is reused as-is (desktop's own map component, see
 * its `mobileControls`/`topInset` props added for this) with a draggable
 * SnapBottomSheet on top holding the results list, so map and list are both
 * always present instead of a toggle between them.
 *
 * Renders its own fixed header (back/logo/search/filters) instead of the
 * generic signed-in/signed-out one -- see hideHeader on AppLayout/
 * LandingLayout in HeavyDutyMachineries.tsx. This header, not SpacesMap's own
 * "Search this area" button, is also this screen's only manual search
 * entry point: panning/zooming the map searches on its own (see SpacesMap's
 * mobileControls auto-debounce).
 *
 * Category picker (MapCategoryPicker, just under the header): lets a guest
 * switch which resource type this whole screen is showing -- Machinery,
 * Accessories, Operators or Services.
 *
 * Machinery is the odd one out and stays that way: its items, filters, paging
 * and search live in the parent (HeavyDutyMachineries.tsx, which is also the
 * desktop page), so here it arrives as props. The other three own their own
 * pipeline through useMapCategorySearch -- three lines each instead of the
 * twenty-five they used to be written out in.
 *
 * Everything the shared map/list/header/popup UI needs then comes off ONE
 * table (see `views` below) rather than a ternary per value. That mattered the
 * moment there were four: a three-way ternary repeated eight times is
 * readable, a four-way one repeated eight times is where somebody adds a
 * category to six of them and nobody notices the two that were missed.
 */
const ExploreMobileMap = forwardRef<SpacesMapHandle, ExploreMobileMapProps>(function ExploreMobileMap(
  {
    items,
    totalCount,
    loading,
    locationSearchSeq,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    searchBarInitialValues,
    onSearchBarSubmit,
    onBoundsChange,
    activeFilterCount,
    onOpenFilters,
    onOpenSpace,
    snapPoint,
    onSnapPointChange,
  },
  ref
) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { isAuthenticated } = useAuth();

  // Signed-in visitors get AppLayout's MobileBottomNav (56px, fixed at the
  // screen's bottom edge) -- but only while the sheet isn't collapsed (see
  // hideBottomNav in HeavyDutyMachineries.tsx): collapsing it hides the nav
  // entirely so the map gets the full screen back, and this area has to
  // grow into the space the nav just gave up. Signed-out visitors render
  // through LandingLayout instead, which has no bottom bar at all -- always
  // 0 regardless of snapPoint for them.
  const bottomNavVisible = isAuthenticated && snapPoint !== 'collapsed';
  const bottomOffset = bottomNavVisible ? BOTTOM_NAV_HEIGHT : 0;

  const [containerHeight, setContainerHeight] = useState(() => window.innerHeight - bottomOffset);
  useEffect(() => {
    const measure = () => setContainerHeight(window.innerHeight - bottomOffset);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [bottomOffset]);

  // How much of the map SnapBottomSheet's current snap point actually
  // covers -- SpacesMap pads its results-fit by this much extra so a fresh
  // search doesn't land a pin behind the sheet. Mirrors SnapBottomSheet's
  // own offsets (see its snapOffsets) rather than reading them back, since
  // there's nothing to read at the moment a fit happens other than which
  // snap point is currently set. 'collapsed' is treated as fully visible --
  // it only leaves a small peek strip at the very bottom, not worth padding
  // a fit for.
  const mapBottomInset =
    snapPoint === 'media' ? containerHeight * (1 - MEDIA_SNAP_RATIO) : snapPoint === 'full' ? containerHeight - OWN_HEADER_HEIGHT : 0;

  const [hoveredSpaceId, setHoveredSpaceId] = useState<string | undefined>(undefined);
  // Which pin's full-size card (below) is currently showing -- replaces the
  // old raise-to-media+scroll-to-card+highlight affordance entirely, and
  // (unlike desktop's still pin-anchored popup) isn't a map popup at all:
  // SpacesMap's mobileControls click handler sets this directly with no
  // Azure Popup involved, since a screen-fixed card has no map position to
  // anchor to. The sheet itself hides while this is set (see
  // SnapBottomSheet's `hidden` prop) rather than the two coexisting;
  // closing the card (its own "x", or tapping a different pin) needs no
  // extra bookkeeping to put the sheet "back" since its own snapPoint was
  // never touched while hidden.
  // Always an array, even for the single pin tap that sets one id: a tap on a
  // cluster badge opens the whole group at once (see SpacesMap's own
  // onSelectCluster), and one code path rendering a carousel of any length --
  // one card included -- beats two paths that have to stay in sync.
  const [popupSpaceIds, setPopupSpaceIds] = useState<string[]>([]);

  // ==================== Category switching ====================
  // All four categories, their searches and their filter overlays come off one
  // shared hook -- see useMapCategoryViews, which the desktop map uses too.
  // Machinery is passed IN rather than owned there: its search lives in the URL
  // (see HeavyDutyMachineries.tsx, which is that screen).
  const { activeCategory, switchCategory, view, filtersModals, resultsLabel } = useMapCategoryViews({
    machinery: {
      items,
      loading,
      totalCount,
      page,
      pageSize,
      onPageChange,
      onPageSizeChange,
      activeFilterCount,
      onOpenFilters,
      searchBarInitialValues,
      onSearchBarSubmit,
      onBoundsChange,
      onOpenSpace,
      locationSearchSeq,
    },
    hoveredItemId: hoveredSpaceId,
  });

  // A pin selection and a hover both belong to whichever category was active
  // when they happened -- switching tabs clears both rather than carrying over
  // a selection that no longer has a matching pin.
  const handleCategoryChange = (category: MapExploreCategory) => {
    switchCategory(category);
    setPopupSpaceIds([]);
    setHoveredSpaceId(undefined);
  };

  // The open pin popups, resolved from the CURRENT category's own rows: a new
  // search that drops everything open leaves this empty on its own, which is
  // what brings the results sheet back with no separate bookkeeping.
  const popupCards: React.ReactNode[] = popupSpaceIds
    .map((pinId) => view.rows.find((row) => row.pinId === pinId)?.card)
    .filter((card): card is React.ReactNode => !!card);

  // Mirrors the sheet's own list-scroll state -- drives the floating "Map"
  // button below (still 'full'-only on its own account, see that button's
  // render condition), on the same signal that fades the sheet's own drag
  // handle in both 'full' and 'media' now.
  const [listScrolled, setListScrolled] = useState(false);
  const listRef = React.useRef<HTMLDivElement>(null);

  // While loading, this swaps for a friendlier line instead of just going
  // blank -- collapsed, all a visitor sees is this row (the map behind it is
  // hidden/being replaced), so an empty label there reads as the screen
  // doing nothing.
  // Just the centered count -- Filters now lives in the fixed header above,
  // not in here (see point 1 of this screen's own redesign brief). Tight
  // top padding keeps this right under the drag handle above it (see
  // SnapBottomSheet's own block/collapsed-peek doc comments).
  const sheetHeader = (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 20px 12px' }}>
      <Text type="secondary">{resultsLabel}</Text>
    </div>
  );

  const iconButtonStyle: React.CSSProperties = {
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: '50%',
    border: 'none',
    background: token.colorFillTertiary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: containerHeight,
        overflow: 'hidden',
        background: token.colorBgContainer,
        transition: 'height 220ms ease',
        // Explicit (rather than relying on implicit "positioned beats
        // static" stacking) so this reliably paints above whatever's
        // underneath in normal document flow -- LandingLayout's own
        // Footer, in particular, sits right below this screen's
        // near-zero-height Content box (this fixed element contributes no
        // layout height of its own) and previously bled through beneath it.
        zIndex: 10,
      }}
    >
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <SpacesMap
          ref={ref}
          items={view.pins}
          onBoundsChange={view.onBoundsChange}
          onSelectItem={(itemId) => setPopupSpaceIds(itemId ? [itemId] : [])}
          onSelectCluster={setPopupSpaceIds}
          onOpenItem={view.openItem}
          hoveredItemId={hoveredSpaceId}
          onHoverItem={setHoveredSpaceId}
          mobileControls
          topInset={MAP_CONTROLS_TOP_INSET}
          bottomInset={mapBottomInset}
          locationSearchSeq={view.locationSearchSeq}
          resultsLoading={view.loading}
        />
      </div>

      {/* Category picker -- just under the fixed header, over the map (see
          this screen's own doc comment). Absolute, not part of the header's
          own flex row, so it stays centered regardless of how much space
          the search bar/filters button take. */}
      <div
        style={{
          position: 'absolute',
          top: OWN_HEADER_HEIGHT + 8,
          left: 0,
          right: 0,
          zIndex: 45,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <MapCategoryPicker active={activeCategory} onChange={handleCategoryChange} />
      </div>

      {/* The sheet's own root spans this whole container (see
          SnapBottomSheet) and is only ever *translated*, not resized, so it
          can be mounted directly here -- no extra pointer-events wrapper
          needed: the part of it currently off-screen (e.g. most of it while
          'collapsed') is clipped away by this container's own overflow:
          hidden and isn't hit-testable, and the actually-visible part
          already re-enables its own pointer-events. headerClearance reserves
          room for the fixed header below without capping how far 'full' can
          slide -- see that prop's own doc comment. */}
      <SnapBottomSheet
        ref={listRef}
        containerHeight={containerHeight}
        headerClearance={OWN_HEADER_HEIGHT}
        snapPoint={snapPoint}
        onSnapPointChange={onSnapPointChange}
        hidden={popupCards.length > 0}
        onListScrolledChange={setListScrolled}
        header={sheetHeader}
      >
        <div style={{ padding: '0 16px 16px' }}>
          {view.loading ? (
            // One wait state for every tab -- the turning gears (see
            // GearLoadingState). No message here: the sheet header right above
            // already says what is being searched.
            <GearLoadingState />
          ) : view.items.length === 0 ? (
            <EmptyState
              icon={<IconSearchOff size={24} />}
              title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_NO_RESULTS)}
              description={translate(
                t,
                activeCategory === 'machinery'
                  ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_NO_RESULTS_DESCRIPTION
                  : TRANSLATIONS.MAP_SEARCH_NO_RESULTS_DESCRIPTION
              )}
            />
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {view.rows.map((row) => (
                  <div
                    key={row.pinId}
                    onMouseEnter={() => {
                      if (snapPoint === 'full') return;
                      setHoveredSpaceId(row.pinId);
                    }}
                    onMouseLeave={() => {
                      if (snapPoint === 'full') return;
                      setHoveredSpaceId(undefined);
                    }}
                    style={{ borderRadius: 16 }}
                  >
                    {row.card}
                  </div>
                ))}
              </div>
              <ListPagination
                current={view.page}
                total={view.totalCount}
                pageSize={view.pageSize}
                onChange={view.setPage}
                onPageSizeChange={view.setPageSize}
              />
            </>
          )}
        </div>
      </SnapBottomSheet>

      {/* This screen's own fixed header, replacing AppHeader/LandingLayout's
          generic one (see hideHeader in HeavyDutyMachineries.tsx) -- always on top
          (explicit z-index, not just DOM order) of both the map and the
          sheet regardless of the sheet's own snap position, including
          'full' where the sheet would otherwise reach all the way to y:0. */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: OWN_HEADER_HEIGHT,
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 12px',
          background: token.colorBgContainer,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        <button type="button" aria-label={translate(t, TRANSLATIONS.BACK)} onClick={() => navigate(-1)} style={iconButtonStyle}>
          <IconArrowLeft size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Same bar, same pill/overlay, every category -- only `mode`
              changes (see HomeSearchBar's own doc comment): Machinery keeps
              its full Tipo/Dónde/Modalidad/Cuándo overlay untouched, the
              other three get the same overlay narrowed to Tipo/Dónde. */}
          <HomeSearchBar
            mode={view.searchBarMode}
            initialValues={view.searchBarInitialValues}
            onSearch={view.onSearchBarSubmit}
            style={{ margin: 0, maxWidth: 'none', padding: '10px 16px', background: token.colorFillTertiary, border: 'none', boxShadow: 'none' }}
          />
        </div>
        <Badge count={view.filterCount} size="small" offset={[-4, 4]}>
          <button
            type="button"
            aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
            onClick={view.openFilters}
            style={iconButtonStyle}
          >
            <IconAdjustmentsHorizontal size={18} />
          </button>
        </Badge>
      </div>

      {/* The selected pin's full-size card, fixed right above the bottom
          nav (this container's own height already excludes it -- see
          containerHeight above) -- replaces the small popup that used to
          anchor to the pin itself. Keyed on the space id so tapping a
          different pin while one is already showing swaps in a fresh
          enter/exit transition instead of the same element's content just
          silently changing underneath it. Reuses the exact same card the
          results list already shows (not a bespoke smaller one), just
          standalone with its own close "x" -- top-left, clear of the
          card's own top-right favorite heart.

          Always through the carousel, even for a single card (which renders
          as a one-slide carousel, visually identical to what a lone card
          always looked like): a tap on a cluster badge opens every item in
          that group at once, and the guest swipes between them sideways --
          the answer to a provider whose items all sit at one address, which
          no amount of zooming ever separates. */}
      <AnimatePresence>
        {popupCards.length > 0 && (
          <motion.div
            key={popupSpaceIds.join('|')}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            // bottom: 0 rather than 16 because the carousel adds its own
            // 16px of padding (see CAROUSEL_PADDING -- without it each card's
            // drop shadow would be clipped by the scroller), which lands a
            // single card in exactly the spot it sat before there was a
            // carousel around it at all.
            style={{ position: 'absolute', left: 16, right: 16, bottom: 0, zIndex: 40 }}
          >
            <SwipeCardCarousel>
              {popupCards.map((card, i) => (
                <div
                  key={i}
                  style={{
                    position: 'relative',
                    background: token.colorBgContainer,
                    borderRadius: 20,
                    padding: 12,
                    boxShadow: '0 8px 28px rgba(0,0,0,0.24)',
                  }}
                >
                  <button
                    type="button"
                    aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAP_POPUP_CLOSE)}
                    onClick={() => setPopupSpaceIds([])}
                    style={{
                      position: 'absolute',
                      // 12 (wrapper padding) + 10 -- keeps this the same 10px
                      // inset from the photo's own corner as before the wrapper
                      // gained its own padding (see its style above).
                      top: 22,
                      left: 22,
                      zIndex: 1,
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      border: 'none',
                      background: 'rgba(255,255,255,0.92)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.24)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <IconX size={16} />
                  </button>
                  {card}
                </div>
              ))}
            </SwipeCardCarousel>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating "Map" button -- only while 'full' and the list has been
          scrolled (same signal driving the sheet's own handle fade), so it
          never shows in collapsed/media where the map is already visible
          without it. Jumps straight to 'collapsed', same destination as
          dragging the sheet all the way down by hand. Hidden whenever a pin
          popup is open too -- the sheet (and this button, which belongs to
          it) aren't relevant while that's showing. */}
      <AnimatePresence>
        {snapPoint === 'full' && listScrolled && popupCards.length === 0 && (
          <motion.button
            key="mobile-map-button"
            type="button"
            onClick={() => onSnapPointChange('collapsed')}
            initial={{ opacity: 0, x: '-50%', y: 8 }}
            animate={{ opacity: 1, x: '-50%', y: 0 }}
            exit={{ opacity: 0, x: '-50%', y: 8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              bottom: 24,
              left: '50%',
              zIndex: 45,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 20px',
              borderRadius: 999,
              border: 'none',
              background: CORAL,
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              boxShadow: '0 4px 14px rgba(0,0,0,0.28)',
              cursor: 'pointer',
            }}
          >
            <IconMap2 size={16} />
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MOBILE_MAP_BUTTON)}
          </motion.button>
        )}
      </AnimatePresence>

      {filtersModals}
    </div>
  );
});

export default ExploreMobileMap;
